import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/auth";
import Entrar from "./paginas/Entrar";
import RedefinirSenha from "./paginas/RedefinirSenha";
import SenhaLiberada from "./paginas/SenhaLiberada";
import Inicio from "./paginas/Inicio";
import Curso from "./paginas/Curso";
import PosGraduacao from "./paginas/PosGraduacao";
import ContrateUmContador from "./paginas/ContrateUmContador";
import Faculdade from "./paginas/Faculdade";
import FaculdadeProva from "./paginas/FaculdadeProva";
import Perfil from "./paginas/Perfil";
import MeusDocumentos from "./paginas/MeusDocumentos";
import Avaliacoes from "./paginas/Avaliacoes";
import Consultorias from "./paginas/Consultorias";
import Abrir from "./paginas/Abrir";
import Admin from "./paginas/Admin";
import Ferramenta from "./paginas/Ferramenta";
import Orcamento from "./paginas/Orcamento";
import FormularioAbertura from "./paginas/FormularioAbertura";
import PesquisaPorConvite from "./paginas/PesquisaPorConvite";
import Diagnostico from "./paginas/Diagnostico";
import DiagnosticoRelatorio from "./paginas/DiagnosticoRelatorio";
import {
  CertificadoGratuitoPagina,
  EmitirGratuito,
  PresencaGratuita,
  VerificarGratuito,
} from "./paginas/CertificadosGratuitos";
import QuadroCfc from "./paginas/QuadroCfc";
import { MeuLivroPorConta } from "./paginas/MeuLivro";
import { Carregando } from "./componentes/ui";

function RotaProtegida({ children }: { children: React.ReactNode }) {
  const { sessao, carregando } = useAuth();
  if (carregando) return <Carregando />;
  if (!sessao) return <Navigate to="/entrar" replace />;
  return children;
}

// Três telas depois do login, e a separação entre elas é a do produto: `/` é a
// visão da conta e as portas dos cursos, `/curso/:slug` é onde as ferramentas
// moram (nunca soltas na inicial) e `/faculdade` é a FBC, com matrícula,
// secretaria e notas.
export default function App() {
  return (
    <Routes>
      <Route path="/entrar" element={<Entrar />} />
      <Route path="/redefinir-senha" element={<RedefinirSenha />} />
      <Route path="/senha-liberada" element={<SenhaLiberada />} />

      {/* A TERCEIRA rota pública do hub, e a primeira que não é do aluno: quem abre
          é o CLIENTE de um aluno, respondendo o formulário de orçamento do IR
          Proposta (000065). Não pode ser protegida — ele não tem conta na CF e não
          vai ter. O que faz o papel da autenticação é o token do formulário, que
          identifica o aluno destinatário, é revogável, e vale só enquanto o acesso
          do aluno estiver vigente (`irp_formulario_publico`). */}
      <Route path="/orcamento/:token" element={<Orcamento />} />

      {/* A QUARTA rota pública, e a segunda que não é do aluno: o CLIENTE de um
          contador responde o questionário de abertura de empresa do Radar
          Simples (000184). Mesma razão da de cima — ele não tem conta na CF e não
          vai ter —, e o token faz o papel da autenticação.

          As duas funções que esta tela chama (`rs_formulario_estado` e
          `rs_formulario_enviar`) são as ÚNICAS do Radar que `anon` alcança, e
          respondem só sobre o token que ele já tem. Na origem, `anon` lia as
          tabelas inteiras com `using (true)`: 309 links e 60 respostas de todo
          mundo, com CPF e endereço de clientes finais dentro. */}
      <Route path="/formulario/:token" element={<FormularioAbertura />} />

      {/* A QUINTA rota pública, e a primeira que é do ALUNO sem ser o login: o
          convite de uma pesquisa de satisfação que chega por e-mail (000320).
          Ele não tem conta no hub — 4 perfis de 67 mil têm, medido em 28/09 —, e
          a decisão de Victor foi o link pessoal, sem login. O token do convite
          faz o papel da autenticação, e as quatro funções que a tela chama são
          as únicas das pesquisas que `anon` alcança: nenhuma devolve o nome nem
          o e-mail de quem responde. */}
      <Route path="/pesquisa/:token" element={<PesquisaPorConvite />} />

      {/* A SEXTA rota pública: o Diagnóstico da Reforma Tributária (000373/000374), que era um
          app da Lovable e veio para o hub por decisão do Enzo (30/10). Quem o faz não tem conta:
          o cadastro devolve um token que deixa enviar, e o relatório se abre pelo token DELE —
          nunca pelo e-mail, como o app antigo deixava qualquer um ler o diagnóstico de outra
          pessoa. As quatro funções que estas telas chamam são as únicas do Diagnóstico que
          `anon` alcança, e as tabelas não são legíveis. */}
      <Route path="/diagnostico" element={<Diagnostico />} />
      <Route path="/diagnostico/relatorio/:token" element={<DiagnosticoRelatorio />} />
      {/* Os cursos gratuitos ao vivo (000380), que eram o certificados-cf: a presença, a emissão,
          o certificado e a validação, todos PÚBLICOS. Quem decide se a presença vale e se o
          certificado sai é o banco, pelas quatro funções que `anon` alcança. */}
      <Route path="/certificados/p/:slug/:aula" element={<PresencaGratuita />} />
      <Route path="/certificados/emitir/:slug" element={<EmitirGratuito />} />
      <Route path="/certificados/verificar/:token" element={<VerificarGratuito />} />
      <Route path="/certificados/:token" element={<CertificadoGratuitoPagina />} />
      {/* O endereço do certificado no site de hoje é `/certificado/<token>`, e é ele que vai nos
          e-mails já mandados. Na virada, o redirecionamento por PREFIXO leva
          `certificados.contabilidadefacilitada.com/*` a `/certificados/*`, e esse endereço chega
          aqui como `/certificados/certificado/<token>`: sem esta rota, ele cairia na de cima com o
          token "certificado" e diria "não encontrado". */}
      <Route path="/certificados/certificado/:token" element={<CertificadoGratuitoPagina />} />
      {/* O quadro da Correção CFC ao vivo (000384), que era o `quadro-correcao-cfc` no Fly.io: PÚBLICO,
          porque vai para a transmissão. A única função que ele chama é a única da Correção que `anon`
          alcança, e ela não devolve a alternativa que a equipe ainda não liberou. */}
      <Route path="/correcao-cfc" element={<QuadroCfc />} />
      <Route
        path="/"
        element={
          <RotaProtegida>
            <Inicio />
          </RotaProtegida>
        }
      />
      <Route
        path="/pos-graduacao"
        element={
          <RotaProtegida>
            <PosGraduacao />
          </RotaProtegida>
        }
      />
      <Route
        path="/contrate-um-contador"
        element={
          <RotaProtegida>
            <ContrateUmContador />
          </RotaProtegida>
        }
      />
      {/* O livro-caixa do CLIENTE MEI (000078). Quem não tem curso nenhum cai
          nele direto pela inicial; esta rota existe para a pessoa que é aluno E
          cliente ao mesmo tempo — a inicial dela é a vitrine, e o livro fica num
          endereço próprio. */}
      <Route
        path="/meu-livro"
        element={
          <RotaProtegida>
            <MeuLivroPorConta quandoNaoCliente={<Navigate to="/" replace />} />
          </RotaProtegida>
        }
      />
      <Route
        path="/curso/:slug"
        element={
          <RotaProtegida>
            <Curso />
          </RotaProtegida>
        }
      />
      {/* A aba da Faculdade é SUB-ROTA, e não estado da página: o aluno volta
          pelo botão do navegador, compartilha o link de uma aba, e recarregar não
          o joga para a primeira. É a mesma decisão das abas do HUB DP.

          `/faculdade` sozinho continua valendo — é a Visão geral. */}
      <Route
        path="/faculdade"
        element={
          <RotaProtegida>
            <Faculdade />
          </RotaProtegida>
        }
      />
      <Route
        path="/faculdade/:aba"
        element={
          <RotaProtegida>
            <Faculdade />
          </RotaProtegida>
        }
      />
      {/* A prova tem rota PRÓPRIA, e não um modal dentro do boletim: o aluno
          passa vários minutos nela, volta pelo botão do navegador e recarrega a
          página. Fica antes de `/faculdade/:aba` na ordem de declaração? Não
          precisa — o React Router casa por especificidade, e "prova" tem dois
          segmentos contra um. */}
      <Route
        path="/faculdade/prova/:disciplinaId"
        element={
          <RotaProtegida>
            <FaculdadeProva />
          </RotaProtegida>
        }
      />
      <Route
        path="/perfil"
        element={
          <RotaProtegida>
            <Perfil />
          </RotaProtegida>
        }
      />
      {/* O ACERVO do aluno (000281, requisito 3.5). Rota PRÓPRIA e sempre a
          mesma, para quem se formou e para quem ainda estuda: o documento
          entregue não expira com o acesso, e duas telas para o mesmo acervo
          divergiriam — a que divergisse em silêncio seria a que ninguém olha. */}
      <Route
        path="/meus-documentos"
        element={
          <RotaProtegida>
            <MeusDocumentos />
          </RotaProtegida>
        }
      />
      {/* As PESQUISAS de satisfação dos cursos do aluno (000310, o G1). Uma
          porta só, no menu da conta: a pesquisa é do curso, mas quem responde é
          a pessoa — e a página do curso avisa quando há uma aberta sobre ele. */}
      <Route
        path="/avaliacoes"
        element={
          <RotaProtegida>
            <Avaliacoes />
          </RotaProtegida>
        }
      />
      {/* O HISTÓRICO da consultoria do portal antigo (000351): só leitura, e no
          menu da conta só para quem tem chamado. A lista e o chamado são a
          mesma página, e o `:id` decide. */}
      <Route
        path="/consultorias"
        element={
          <RotaProtegida>
            <Consultorias />
          </RotaProtegida>
        }
      />
      <Route
        path="/consultorias/:id"
        element={
          <RotaProtegida>
            <Consultorias />
          </RotaProtegida>
        }
      />
      {/* A ponte para os destinos que precisam de credencial gerada na hora
          (Fase 4): o card é um link comum para cá, e esta tela pede o endereço à
          Edge Function `abrir-card` e redireciona. Protegida como as outras —
          sem sessão não há o que abrir. */}
      <Route
        path="/abrir/:curso/:card"
        element={
          <RotaProtegida>
            <Abrir />
          </RotaProtegida>
        }
      />

      {/* A mesma ponte, com uma AULA no fim (000177): os cursos bônus da Pós
          Finanças mandam o aluno logado para a aula, e não para a raiz da escola.
          É uma rota a mais e não um `onClick` na lista pelas três razões medidas
          que `Abrir.tsx` documenta — bloqueador de pop-up, `window.opener` numa
          aba de terceiro, e clique que parece não funcionar enquanto a rede
          responde. Com rota, cada aula volta a ser um LINK: ctrl+clique e "abrir
          em nova aba" funcionam. */}
      <Route
        path="/abrir/:curso/:card/:aula"
        element={
          <RotaProtegida>
            <Abrir />
          </RotaProtegida>
        }
      />

      {/* As ferramentas que moram AQUI DENTRO (`cards.destino = 'interno'`,
          000065). Uma rota por slug de card, e não uma por ferramenta: quem deriva
          a rota é `aberturaDoCard`, num lugar só, e MEI PRO e IR PRO entram com uma
          linha no despacho de `Ferramenta`. Protegida — e quem confere se o aluno
          tem ESTA ferramenta é a vitrine que vem do banco, não o guard de rota. */}
      <Route
        path="/ferramenta/:slug/*"
        element={
          <RotaProtegida>
            <Ferramenta />
          </RotaProtegida>
        }
      />

      {/* O painel de operação. A rota é protegida como qualquer outra (precisa de
          sessão), e QUEM é operador é decidido dentro de `Admin` — pelo banco,
          não aqui: `painel_admin()` devolve zero linha para quem não passa por
          `eh_operador()`. Guard de rota em JavaScript é conforto de navegação;
          burlá-lo no navegador dá uma tela vazia, não dado de ninguém. */}
      <Route path="/admin" element={<Navigate to="/admin/painel" replace />} />
      <Route
        path="/admin/:aba"
        element={
          <RotaProtegida>
            <Admin />
          </RotaProtegida>
        }
      />
      {/* O segundo segmento é a SUB-ABA, e existe para o operador poder mandar o
          link de um painel específico a um colega (`/admin/faculdade/notas`).
          Quem lê o `:sub` é o componente da aba — hoje só `AdminFaculdade`; as
          outras abas ignoram e continuam funcionando pela rota de um segmento. */}
      <Route
        path="/admin/:aba/:sub"
        element={
          <RotaProtegida>
            <Admin />
          </RotaProtegida>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
